import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import StudyUsers from '@/components/Studies/Show/Users';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef, FilterOption } from '@/components/UI/Table/TableComponent.model';
import { _notNil } from '@/littledash';
import BenchlingEntityLink from '@/components/UI/BenchlingEntityLink/BenchlingEntityLink';
import type { Study } from '@/model/Study.model';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { api as apiRoutes, web as webRoutes } from '@/support/route';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { StudyTableProps } from './Dashboard.model';
import { generateColumns, generateStudiesMetadataColumns, studiesfilterOptions } from './Dashboard.utils';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';
import type { StudyForm } from '@/model/StudyForm.model';
import { useFetchCollection } from '@/support/Hooks/fetch';

const initColumns: Array<TanStackColumnDef<Study>> = [
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    enableSorting: true,
    isVisible: true,
    cell: ({ row }: { row: { original: Study } }) => {
      const original = row.original;
      const hasBenchlingSync = original.synced_to_benchling === true;
      return (
        <div className="flex items-center">
          <BenchlingEntityLink
            entityType="studies"
            apiId={original.api_id}
            benchlingEntityWebUrl={original.benchling_entity_web_url}
            visible={hasBenchlingSync}
          />
          <Link
            className="link"
            to={webRoutes('studies.show', {
              id: original.id,
            })}
          >
            {original.name}
          </Link>
        </div>
      );
    },
  },
  {
    id: 'code',
    header: 'Study Code',
    accessorKey: 'code',
    enableSorting: true,
    isVisible: false,
  },
  {
    id: 'benchling_entity_id',
    header: 'Registry API ID',
    accessorKey: 'benchling_entity_id',
    enableSorting: false,
    isVisible: false,
    cell: ({ row }: { row: { original: Study } }) => row.original.benchling_entity_id ?? '-',
  },
  {
    id: 'type',
    header: 'Type',
    isVisible: true,
    enableSorting: true,
    accessorFn: (row: Study) => row.settings?.title,
  },
  {
    id: 'team',
    header: 'Team',
    enableSorting: false,
    isVisible: true,
    accessorKey: 'users',
    cell: ({ row }: { row: { original: Study } }) => <StudyUsers study={row.original} />,
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorKey: 'created_at',
    enableSorting: true,
    isVisible: true,
    cell: ({ row }: { row: { original: Study } }) => <DateTimeRenderer value={row.original.created_at} />,
  },
  {
    id: 'archived_at',
    header: 'Completed',
    accessorKey: 'archived_at',
    enableSorting: true,
    isVisible: true,
    cell: ({ row }: { row: { original: Study } }) => <DateTimeRenderer value={row.original.archived_at} />,
  },
];

const ArchivedTable: FC<StudyTableProps> = ({ studyTypes, studyMetadataFilters, hasStudyCode }) => {
  const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>([]);
  const [columns, setColumns] = useState<Array<TanStackColumnDef<Study>>>(initColumns);
  const [apiError, setAPIError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const { collection: studyForms, collectionLoading: studyFormsFetching } = useFetchCollection<StudyForm>({
    collectionType: 'teamStudiesForms',
    queryParams: { perPage: -1 },
  });

  useEffect(() => {
    if (_notNil(studyTypes)) {
      setFilterOptions(studiesfilterOptions(studyTypes, studyMetadataFilters));
    }
  }, [studyTypes, studyMetadataFilters]);

  useEffect(() => {
    if (_notNil(studyTypes) && _notNil(studyMetadataFilters) && !studyFormsFetching && hasStudyCode !== undefined) {
      generateStudiesMetadataColumns(studyMetadataFilters)
        .then((metadata) => {
          const baseColumns = initColumns.map((col) => (col.id === 'code' ? { ...col, isVisible: hasStudyCode } : col));
          setColumns(
            generateColumns(
              baseColumns,
              metadata.map((m) => ({ id: m.id, header: m.header ?? '', slug: m.slug })),
              navigate,
              studyForms
            )
          );
          setLoading(false);
        })
        .catch((error) => {
          if (notAborted(error)) {
            setLoading(false);
            setAPIError(error);
          }
        });
    }
  }, [studyTypes, studyMetadataFilters, studyFormsFetching, hasStudyCode]);

  if (loading) {
    return null;
  }

  return (
    <>
      {apiError ? (
        <ApiErrorBanner className="mb4" errorType={ApiErrorType.FETCH} error={apiError} />
      ) : (
        <div className="pa4">
          <APITable
            columns={columns}
            apiInfo={{ type: 'legacyInternalApi', route: apiRoutes('studies.archived') }}
            reduxTableName="dashboardArchive"
            filterOptions={filterOptions}
            showViewSelector
            customFilterProcessor={createMultiSelectDoubleEncodingProcessor(studyMetadataFilters ?? [])}
            searchPlaceholderText="Search by study name or study code"
          />
        </div>
      )}
    </>
  );
};

export default ArchivedTable;
